<template>
    <div
        class="field-editor"
        :data-vote-mode="String(fieldEditorService.isShowComments)"
        :class="fieldEditorService.statusClass"
        :style="[{'position': !fieldEditorService.draggableDialogMode ? 'static': 'fixed'}, fieldEditorService.editorStyles]"
        @mousedown.stop
    >
        <div
            v-if="fieldEditorService.draggableDialogMode"
            :class="['draggable-area', 'draggable-area__top']"
            @mousedown.stop="handleDrag($event)"
        >
            <div>
                <PzIcon class="drag-icon" icon="SolidMove" />
            </div>
        </div>
        <div class="field-editor-body" @click.stop @mousedown.stop>
            <div
                :class="['draggable-area', 'draggable-area__left', {'draggable-area__draggableDialogMode': !fieldEditorService.draggableDialogMode}]"
                @mousedown.stop="handleDrag($event)"
            />
            <main class="field-editor-wrap">
                <component
                    :is="comp"
                    v-if="fieldEditorService.field"
                    :field="fieldEditorService.field"
                    :structor.sync="fieldEditorService.structor"
                    :value="fieldEditorService.value"
                    :row="fieldEditorService.row"
                    class="field-function"
                    @change="handleChange"
                />
                <footer v-if="fieldEditorService.isPatentField" class="field-editor-wrap-footer">
                    <label class="field-editor-wrap-footer-voteMode">
                        <label :style="{'vertical-align': !fieldEditorService.draggableDialogMode ? 'bottom' : 'unset'}" @click="fieldEditorService.toggleVoteMode()">
                            <div
                                class="workspace-simple-switch"
                                :class="{
                                    active: fieldEditorService.voteStatus.enabled,
                                    disabled: !fieldEditorService.voteStatus.allow,
                                }"
                            />
                            <span class="field-editor-wrap-footer-voteMode-tips"> {{ $t('snapBusiness.comment.voteMode') }} </span>
                        </label>
                        <ElTooltip
                            effect="light"
                            placement="top"
                            popper-class="pt-ui-tooltip votemode-tip-popper"
                            :visible-arrow="false"
                            :open-delay="100"
                        >
                            <template #content>
                                <p v-html="fieldEditorService.voteStatus.allow ? $t('snapBusiness.comment.voteTip') : $t('snapBusiness.comment.voteDisabled')" />
                            </template>
                            <span class="tip-icon">
                                <PzIcon icon="SolidInfo" style="font-size: 24px" />
                            </span>
                        </ElTooltip>
                    </label>
                    <label
                        v-if="fieldEditorService.voteStatus.enabled"
                        class="field-editor-wrap-footer-comments"
                        @click="fieldEditorService.toggleComments(!fieldEditorService.isShowComments)"
                    >
                        <span style="vertical-align: middle;"> {{ $t('snapBusiness.comment.commentsNum', { num: fieldEditorService.voteStatus.data?.comment_info.comment_count}) }} </span>
                        <PzIcon :style="{'margin-bottom': !fieldEditorService.draggableDialogMode ? '3px' : '-1px'}" :icon="fieldEditorService.isShowComments ? 'SolidArrowLeft' : 'SolidArrowRight'" />
                    </label>
                </footer>
                <template v-if="fieldEditorService.isShowComments">
                    <i class="vertical-split-line" />
                    <aside class="field-editor-comments">
                        <CommentsList
                            v-if="fieldEditorService.field && fieldEditorService.row"
                            class="comments-list"
                            :field-id="fieldEditorService.field.field_id"
                            :space-id="fieldEditorService.spaceId"
                            :folder-id="fieldEditorService.folderId"
                            :patent-id="fieldEditorService.row.PATENT_ID"
                            :vote-mode="true"
                            :time-zone="fieldEditorService.utcOffset"
                            :currentUser="fieldEditorService.currentUser"
                            :permission="fieldEditorService.permissionsForComment"
                            :comments="fieldEditorService.voteStatus.data?.comment_info.comments"
                            @update:comments="$set(fieldEditorService.row.COMMENT, 'comments', $event)"
                        />
                    </aside>
                </template>
            </main>
            <div
                :class="['draggable-area', 'draggable-area__right', {'draggable-area__draggableDialogMode': !fieldEditorService.draggableDialogMode}]"
                @mousedown.stop="handleDrag($event)"
            />
        </div>
        <div
            :class="['draggable-area', 'draggable-area__bottom', {'draggable-area__draggableDialogMode': !fieldEditorService.draggableDialogMode}]"
            @mousedown.stop="handleDrag($event)"
        />
        <div
            v-if="fieldEditorService.draggableDialogMode"
            class="resize-area"
            @mousedown.stop="handleResize"
        >
            <PzIcon class="resize-icon" icon="SolidArrowsmallDown" />
        </div>
    </div>
</template>

<script setup lang="ts">
import '@patsnap-ui/icon/assets/solid/move.svg';
import '@patsnap-ui/icon/assets/solid/ArrowsmallDown.svg';
import '@patsnap-ui/icon/assets/solid/ArrowLeft.svg';
import '@patsnap-ui/icon/assets/solid/ArrowRight.svg';

import { Icon as PzIcon } from 'patsnap-biz';
import { Tooltip } from 'element-ui';
import type { FieldEditorService } from './index';
import CommentsList from './comments/CommentsList.vue';
import _ from 'lodash';
import { computed, nextTick, onBeforeUnmount, onMounted, provide, watch } from 'vue';

const ElTooltip = Tooltip;

const comp = computed(() => {
    const type = fieldEditorService.value.field?.field_type;
    if (type === 'LAYER') {
        return () => import('./LayerEditor.vue')
    }
    else if (type === 'OPTION') {
        return () => import('./OptionEditor.vue')
    }
    return type;
})

const props = defineProps<{
    service: FieldEditorService;
}>();

const fieldEditorService = computed(() => props.service);

provide('fieldEditorService', fieldEditorService);

function handleDrag(event: MouseEvent) {
    fieldEditorService.value.dragEditor(event);
}

function handleResize() {
    fieldEditorService.value.resizeEditor();
}

function handleChange(data: any) {
    fieldEditorService.value.setNewValue(data, true);
}

watch(() => fieldEditorService.value.voteStatus.enabled, () => {
    if (fieldEditorService.value.voteStatus.enabled) {
        fieldEditorService.value.updateVoteCommentsInfo();
    }
},{ immediate: true });

let $backDrop: HTMLElement | undefined = undefined;

function onClickOutside() {
    fieldEditorService.value.hide();
}

onMounted(() => {
    if (fieldEditorService.value.draggableDialogMode) {
        nextTick(() => {
            $backDrop = document.createElement('div');
            $backDrop.className = 'field-editor__backdrop';
            document.body.insertBefore($backDrop, document.body.firstChild);
            $backDrop.onclick = () => {
                onClickOutside()
            }
        })
    } else {
        fieldEditorService.value.setEditor(fieldEditorService.value.isShowComments);
    }
    fieldEditorService.value.initEditorRect();
});

onBeforeUnmount(() => {
    $backDrop?.remove();
    fieldEditorService.value.filterWords = '';
});
</script>

<style lang="scss">
.votemode-tip-popper {
    max-width: 540px;
    line-height: 16px !important;
    background: red;
}
body[data-field-editor-dragging=true] {
    * {
        user-select: none;
    }
}
.field-editor__draging,
.field-editor__resizing {
    user-select: none;
    -webkit-user-select: none;

    .deep-view__content-ws,
    .target-sequence__drawer {
        pointer-events: none;
    }
}

.field-editor__draging {
    * {
        cursor: grabbing !important;
        -webkit-cursor: grabbing !important;
    }
}
.field-editor__backdrop{
    position: fixed;
    top:0;left:0;bottom:0;right:0;
    background: none;z-index: 998;
    user-select: none;
}
.field-editor {
    position: fixed;
    background: $bg-white;
    box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.08),
    0px 2px 6px 0px rgba(0, 0, 0, 0.08);
    border-radius: 4px;
    border: 1px solid $border-light;
    display: flex;
    flex-direction: column;
    z-index: 999;

    .draggable-area {
        border-radius: 3px 3px 0px 0px;
        cursor: grab;
        user-select: none;
        -webkit-user-select: none;
        display: flex;

        &__top {
            height: 16px;

            > div {
                width: 100%;
                height: 11px;
                display: flex;
                align-items: center;
                justify-content: center;
                background: $status-info-light;
            }
        }

        &__left {
            width: 8px;
        }

        &__right {
            width: 8px;
        }

        &__bottom {
            height: 8px;
        }

        &__draggableDialogMode {
            cursor: unset !important;
        }
    }

    .field-editor-body {
        display: flex;
        flex: 1;
        flex-direction: row;
        overflow: hidden;
    }

    .drag-icon {
        font-size: 16px;
        transform: rotate(90deg);
        color: $fill-normal;
    }

    .resize-area {
        position: absolute;
        right: -6px;
        bottom: -9px;
        cursor: nwse-resize;
        z-index: 1;

        .resize-icon {
            font-size: 24px;
            transform: rotate(-45deg);
            color: $fill-normal;
        }
    }
}

.field-editor-wrap {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 8px 1fr;
    grid-template-rows: 1fr auto;
    grid-gap: 8px;
    grid-template-areas:
            "func func func"
            "vote vote vote";

    [data-vote-mode="true"] & {
        grid-template-areas:
            "func split comments"
            "vote vote vote";
    }

    > .field-function {
        grid-area: func;
        overflow-y:auto;
    }

    > footer {
        grid-area: vote;
    }

    > .vertical-split-line {
        grid-area: split;
    }

    > .comments-list {
        grid-area: comments;
    }

    .vertical-split-line {
        width: 0;
        border-left: 1px solid $border-light;
        display: block;
    }

    .horizontal-split-line {
        width: 100%;
        height: 0;
        border-top: 1px solid $border-light;
        display: block;
    }

    .option-editor {
        display: flex;
        flex-grow: 1;
        width: 100%;

        .option-custom-field-select-and-edit-comp-common {
            width: 100%;

            .field-pane {
                margin-top: 8px;
            }
        }
    }

    .comments-list {
        max-width: 400px;
        height: 100%;
        .comment {
            height: 100%;
        }
    }

    &-footer {
        border-top: 1px solid $border-light;
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 36px;
        padding-top: 8px;
        &-comments {
            height: 20px;
            line-height:20px;
            cursor: pointer;
            color: #1976d2;
            font-size: 14px;

            .patsnap-biz-icon {
                font-size: 20px;
                color: #1976d2;
            }
            &:hover {
                color: #1667B8;
                .patsnap-biz-icon {
                    font-size: 20px;
                    color: #1667B8;
                }
            }
        }
        &-voteMode {
            height: 20px;
            line-height:20px;
            user-select: none;
            cursor: pointer;
            &-tips {
                margin-right: 2px;
                font-size: 14px;
                color: $text-primary;
            }
            .workspace-simple-switch {
                transform: translateY(2px);
                margin-right: 4px;
                cursor: pointer;
                display: inline-block;
                width: 22px;
                height: 14px;
                border-radius: 10px;
                background: $fill-hint;
                position: relative;
                line-height:20px;
                &:before {
                    display: inline-block;
                    content: '';
                    background: $bg-white;
                    width: 14px - 2 * 2px;
                    height: 14px - 2 * 2px;
                    position: absolute;
                    left: 2px;
                    top: 2px;
                    border-radius: 50%;
                }
                &.disabled {
                    background: $bg-darker;
                    cursor: not-allowed;
                }
                &.active {
                    background: $brand-primary;
                    &.disabled {
                        background: $brand-disable;
                    }
                    &:before {
                        left: initial;
                        right: 2px;
                    }
                }
            }
            .tip-icon {
                [icon="SolidInfo"] {
                    font-size: 22px !important;
                    cursor: pointer;
                    color: $fill-hint;
                    &:hover {
                        color: $fill-hover;
                    }
                }
            }
        }

    }
}

.field-editor-comments {
    height: 100%;
    overflow: hidden;
    &-line {
        height: 1px;
        width: 100%;
        background: #ebecf0;
    }
    &-nodata {
        height: calc(100% - 155px);
        padding-top: 40px;
        p {
            font-size: 14px;
            color: $text-secondary;
        }
        text-align: center;
    }
}

.field-editor__thumbup-icon {
    fill: $fill-hint;
    cursor: pointer;
    &[data-is-active=true] {
        fill: $brand-primary;
    }
}
</style>
